import { ComponentProps, forwardRef } from 'react';
import { tv, VariantProps } from 'tailwind-variants';

import { Spinner } from '@/assets/Spinner';
import { clsx } from '@/utils/helpers';

export const button = tv({
  base: [
    'relative inline-flex items-center justify-center text-center',
    'rounded-md px-4 py-2.5 leading-5 font-medium whitespace-nowrap shadow-sm',
    'focus-visible:ring-zmyle-secondary outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'transition hover:cursor-pointer active:scale-95 disabled:active:scale-100',
    'disabled:cursor-not-allowed disabled:opacity-75',
  ],

  variants: {
    variant: {
      primary: 'bg-zmyle-secondary text-white',
      ghost: 'px-2 shadow-none',
      outline: 'p-2 shadow-[0_0_5px_rgba(0,0,0,0.29)]',
      text: 'hover:text-dynamic-accent text-base shadow-none',
      link: 'text-dynamic-font hover:text-dynamic-accent p-0 font-normal shadow-none hover:underline',
      store: [
        'from-store-secondary to-store-primary bg-gradient-to-r text-white shadow-lg',
        'hover:from-store-primary hover:to-store-secondary',
      ],
      benefit: [
        'from-benefit-secondary to-benefit-primary bg-gradient-to-r text-white shadow-lg',
        'hover:from-benefit-primary hover:to-benefit-secondary',
      ],
      network: [
        'from-network-secondary to-network-primary bg-gradient-to-r text-white shadow-lg',
        'hover:from-network-primary hover:to-network-secondary',
      ],
      zmyle: [
        'from-zmyle-primary to-zmyle-secondary bg-gradient-to-r text-white shadow-lg',
        'hover:from-zmyle-secondary hover:to-zmyle-primary',
      ],
    },
  },

  defaultVariants: {
    variant: 'primary',
  },
});

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof button> & {
    isLoading?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, isLoading = false, children, disabled, ...props },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        className={button({ variant, className })}
        disabled={disabled || isLoading}
        aria-disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading && (
          <Spinner
            className="absolute inset-0 top-1/2 left-1/2 -translate-1/2"
            aria-hidden="true"
          />
        )}

        {isLoading ? (
          <span className={clsx(isLoading && 'invisible')}>{children}</span>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
